import { useEffect, useMemo, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';

import { BLOOM_DURATION } from '../constants';

const TAU = Math.PI * 2;

export default function KaleidoscopeStage({
  availableForDrawing,
  bloomCanvasRef,
  bloomClassName,
  bloomKey,
  brushColor,
  canvasSize,
  containerRef,
  folds,
  isRevealed,
  onBrushColorChange,
  onWedgeFocusScaleChange,
  displayStartAngle,
  revealStartAngle,
  strokeBudgetSlots,
  usedBudget,
  wedgeCanvasRef,
}) {
  const t = useTranslations('common.newspaper.art_games.kaleidoscope.stage');
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const wrapper = containerRef.current;
    if (!wrapper) return undefined;

    const measure = () => {
      const width = wrapper.clientWidth;
      const height = wrapper.clientHeight;
      setViewportSize((previous) => (
        previous.width === width && previous.height === height
          ? previous
          : { width, height }
      ));
    };

    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(wrapper);
    window.addEventListener('resize', measure);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [containerRef]);

  const rotationDelta = useMemo(
    () => {
      let delta = revealStartAngle - displayStartAngle;
      while (delta <= -Math.PI) delta += TAU;
      while (delta > Math.PI) delta -= TAU;
      return delta;
    },
    [displayStartAngle, revealStartAngle],
  );
  const wedgeStyle = useMemo(() => {
    const wedgeAngle = TAU / folds;
    const normalizedStart = ((displayStartAngle % TAU) + TAU) % TAU;
    const endAngle = normalizedStart + wedgeAngle;
    const polarToPercent = (angle) => ({
      x: 50 + Math.cos(angle) * 49,
      y: 50 + Math.sin(angle) * 49,
    });

    const startPoint = polarToPercent(normalizedStart);
    const endPoint = polarToPercent(endAngle);
    const polygonPoints = [{ x: 50, y: 50 }, startPoint];

    const arcSegments = Math.max(12, Math.ceil(wedgeAngle / (Math.PI / 24)));
    for (let index = 1; index < arcSegments; index += 1) {
      const angle = normalizedStart + (wedgeAngle * index) / arcSegments;
      polygonPoints.push({
        x: 50 + Math.cos(angle) * 49,
        y: 50 + Math.sin(angle) * 49,
      });
    }
    polygonPoints.push(endPoint);

    const bounds = polygonPoints.reduce(
      (accumulator, point) => ({
        minX: Math.min(accumulator.minX, point.x),
        maxX: Math.max(accumulator.maxX, point.x),
        minY: Math.min(accumulator.minY, point.y),
        maxY: Math.max(accumulator.maxY, point.y),
      }),
      { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity },
    );

    const contentWidth = Math.max(bounds.maxX - bounds.minX, 1);
    const contentHeight = Math.max(bounds.maxY - bounds.minY, 1);
    const viewportWidth = viewportSize.width || canvasSize;
    const viewportHeight = viewportSize.height || canvasSize;
    const focusScale = Math.max(1, Math.min(
      (viewportWidth - 16) / (canvasSize * contentWidth / 100),
      (viewportHeight - 16) / (canvasSize * contentHeight / 100),
    ));
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;

    return {
      '--wedge-clip': `polygon(${polygonPoints.map((point) => `${point.x.toFixed(3)}% ${point.y.toFixed(3)}%`).join(', ')})`,
      '--wedge-focus-x': `${((50 - centerX) / 100 * canvasSize * focusScale).toFixed(2)}px`,
      '--wedge-focus-y': `${((50 - centerY) / 100 * canvasSize * focusScale).toFixed(2)}px`,
      '--wedge-focus-scale': focusScale.toFixed(3),
      '--wedge-rotation': `${rotationDelta}rad`,
      '--bloom-duration': `${BLOOM_DURATION}ms`,
    };
  }, [canvasSize, displayStartAngle, folds, rotationDelta, viewportSize]);

  useEffect(() => {
    onWedgeFocusScaleChange(Number(wedgeStyle['--wedge-focus-scale']));
  }, [onWedgeFocusScaleChange, wedgeStyle]);

  return (
    <section
      className={`kaleidoscope-stage ${isRevealed ? 'is-revealed' : ''}`}
      aria-label={t('aria.drawing_wedge')}
      style={wedgeStyle}
    >
      <div className="kaleidoscope-stage__stack">
        <div className="kaleidoscope-stage__rail">
          <div className="kaleidoscope-budget" aria-label={`${t('strokes_label')}: ${usedBudget}/${strokeBudgetSlots.length}`}>
            {strokeBudgetSlots.map((slot) => (
              <span
                key={`stroke-slot-${slot}`}
                className={`kaleidoscope-budget__dot ${slot < usedBudget ? 'is-used' : ''}`}
                aria-hidden="true"
              />
            ))}
            <span className="kaleidoscope-budget__label">{t('strokes_label')}</span>
          </div>
          <input
            type="color"
            className="kaleidoscope-stage__color-picker"
            value={brushColor}
            onChange={(event) => onBrushColorChange(event.target.value)}
            aria-label={t('brush_color')}
            title={t('brush_color')}
            disabled={isRevealed}
          />
        </div>
        <div className="kaleidoscope-stage__canvas-wrapper" ref={containerRef}>
          <div className="kaleidoscope-stage__canvas-stack" style={{ width: canvasSize, height: canvasSize }}>
            <canvas
              ref={wedgeCanvasRef}
              className={`kaleidoscope-canvas ${availableForDrawing ? 'is-active' : 'is-locked'}`}
            />
            <canvas
              key={bloomKey}
              ref={bloomCanvasRef}
              className={bloomClassName}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
